/* global navigator, fetch */
import React, { useEffect, useState } from 'react';
import { BuilderComponent, builder, useIsPreviewing } from '@builder.io/react';
import { useLocation } from 'react-router-dom';
import { NotFoundSoRedirect } from '../../NotFoundSoRedirect';

builder.init(import.meta.env.REACT_APP_PUBLIC_BUILDER_KEY);

export default function BuilderPage() {
  const isPreviewingInBuilder = useIsPreviewing();
  const location = useLocation();
  const [notFound, setNotFound] = useState(false);
  const [content, setContent] = useState(null);

  useEffect(() => {
    const hostname = window.location.hostname;
    const href = window.location.href;

    // Builder.io SDK automatically handles published vs draft content
    // - On production: only published content is shown
    // - In preview mode: draft content is available
    // No domain restriction needed as the SDK provides these safeguards

    // Add comprehensive debugging for Builder.io preview issues
    console.group('🔧 Builder.io Debug Information');

    // Environment detection
    console.log('📍 Environment Info:', {
      hostname: window.location.hostname,
      origin: window.location.origin,
      pathname: window.location.pathname,
      search: window.location.search,
      href: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Builder.io preview detection
    console.log('🔍 Builder.io Context:', {
      isPreviewingInBuilder,
      isBuilderPreviewDomain: window.location.hostname.includes('preview.builder.codes'),
      builderQueryParams: {
        hasBuilderPreview: window.location.search.includes('builder.preview'),
        hasBuilderEdit: window.location.search.includes('builder.edit'),
        searchParams: new URLSearchParams(window.location.search).toString(),
      },
    });

    // Headers and CORS debugging
    fetch(window.location.href, { method: 'HEAD' })
      .then((response) => {
        console.log('🌐 Response Headers (for current page):', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          cors: {
            accessControlAllowOrigin: response.headers.get('access-control-allow-origin'),
            accessControlAllowMethods: response.headers.get('access-control-allow-methods'),
            accessControlAllowHeaders: response.headers.get('access-control-allow-headers'),
            xFrameOptions: response.headers.get('x-frame-options'),
            contentSecurityPolicy: response.headers.get('content-security-policy'),
          },
        });
      })
      .catch((error) => {
        console.error('❌ Failed to fetch headers:', error);
      });

    // Service Worker debugging
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        console.log('🔧 Service Worker Registrations:', {
          count: registrations.length,
          registrations: registrations.map((reg) => ({
            scope: reg.scope,
            scriptURL: reg.scriptURL,
            state: reg.installing ? 'installing' : reg.waiting ? 'waiting' : reg.active ? 'active' : 'unknown',
          })),
        });
      });
    }

    console.groupEnd();

    async function fetchContent() {
      console.log('📄 Fetching Builder.io content for:', window.location.pathname);

      try {
        const fetchedContent = await builder.get('page', { url: window.location.pathname }).promise();
        console.log('✅ Builder.io content fetch result:', {
          found: !!fetchedContent,
          content: fetchedContent ? 'Content loaded successfully' : 'No content found',
          title: fetchedContent?.data?.title || 'No title',
        });

        setContent(fetchedContent);
        setNotFound(!fetchedContent);
        if (fetchedContent?.data.title) {
          document.title = fetchedContent.data.title;
        }
      } catch (error) {
        console.error('❌ Builder.io content fetch error:', error);
        setNotFound(true);
      }
    }
    fetchContent();
  }, [isPreviewingInBuilder, location.pathname]);

  // Show appropriate message based on state
  if (content === null) return null;
  if (notFound && !isPreviewingInBuilder) return <NotFoundSoRedirect />;

  return <BuilderComponent model='page' content={content} />;
}
